import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  AuthService,
  AutoLoginService,
  Installation,
  QbusLocaleService,
} from '@ubie/core';

const DISPLAYED_COLUMNS: string[] = [
  'name',
  'address',
  'owner',
  'added',
  'favorite',
  'actions',
];

function emptyPaginatorIntl(): MatPaginatorIntl {
  const intl = new MatPaginatorIntl();
  intl.firstPageLabel = '';
  intl.lastPageLabel = '';
  intl.nextPageLabel = '';
  intl.previousPageLabel = '';
  return intl;
}

@Component({
  selector: 'acl-installation-list',
  standalone: false,
  templateUrl: './installation-list.component.html',
  styleUrl: './installation-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatPaginatorIntl, useFactory: emptyPaginatorIntl }],
})
export class InstallationListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly autoLoginService = inject(AutoLoginService);
  private readonly qbusLocaleService = inject(QbusLocaleService);

  readonly installations = input<Installation[] | undefined>(undefined);
  readonly managedInstallationIds = input<Set<number>>(new Set());
  readonly showOnlyManaged = signal(false);

  readonly canShowManagedToggle = computed(
    () =>
      this.authService.hasClaim('CanAccessInstallationsAll') &&
      this.authService.hasClaim('CanAccessInstallationsManaged') &&
      this.managedInstallationIds().size > 0,
  );

  private readonly filteredInstallations = computed(() => {
    const all = this.installations() ?? [];
    if (!this.showOnlyManaged()) return all;
    const managedIds = this.managedInstallationIds();
    return all.filter((row) => managedIds.has(row.id));
  });

  readonly hasInstallation = computed(
    () => this.filteredInstallations().length > 0,
  );
  readonly installationCount = computed(
    () => this.filteredInstallations().length,
  );
  readonly columns = input<any[]>();

  readonly displayedColumns = DISPLAYED_COLUMNS;

  readonly dataSource = new MatTableDataSource<Installation>();

  @ViewChild(MatPaginator)
  set paginator(paginator: MatPaginator | undefined) {
    if (!paginator) {
      return;
    }
    this.dataSource.paginator = paginator;
  }

  @ViewChild(MatSort)
  set sort(sort: MatSort | undefined) {
    if (!sort) {
      return;
    }
    this.dataSource.sort = sort;
  }

  private readonly sortedInstallations = computed(() =>
    [...this.filteredInstallations()].sort(
      (a: Installation, b: Installation) =>
        (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0),
    ),
  );

  private readonly syncDataEffect = effect(() => {
    this.dataSource.data = this.sortedInstallations();
  });

  ngOnInit(): void {
    this.dataSource.filterPredicate = this.installationFilterPredicate;
    this.dataSource.sortingDataAccessor = this.installationSortingDataAccessor;
    this.dataSource.sortData = (
      data: Installation[],
      sort: MatSort,
    ): Installation[] => {
      if (!sort.active || sort.direction === '') {
        // No active sort: keep favourites pinned to top
        return [...data].sort(
          (a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0),
        );
      }
      // Active sort: normal sort behaviour, no pinning
      const dir = sort.direction === 'asc' ? 1 : -1;
      return [...data].sort((a, b) => {
        const aVal = this.installationSortingDataAccessor(a, sort.active);
        const bVal = this.installationSortingDataAccessor(b, sort.active);
        return (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) * dir;
      });
    };
  }

  private installationFilterPredicate(
    data: Installation,
    filter: string,
  ): boolean {
    const term = filter.trim().toLowerCase();
    if (!term) {
      return true;
    }
    const fields: (string | undefined)[] = [
      data.name,
      (data as any).addressLine1,
      (data as any).postalCode,
      (data as any).city,
      data.owner?.firstname,
      data.owner?.lastname,
    ];
    return fields
      .filter((field): field is string => Boolean(field))
      .join(' ')
      .toLowerCase()
      .includes(term);
  }

  private installationSortingDataAccessor(
    data: Installation,
    sortHeaderId: string,
  ): string | number {
    switch (sortHeaderId) {
      case 'name':
        return (data.name ?? '').toLowerCase();
      case 'address': {
        const address =
          `${(data as any).addressLine1 ?? ''} ${(data as any).postalCode ?? ''} ${(data as any).city ?? ''}`.trim();
        return address.toLowerCase();
      }
      case 'owner': {
        const owner = data.owner
          ? `${(data.owner as any).firstname ?? ''} ${(data.owner as any).lastname ?? ''}`.trim()
          : '';
        return owner.toLowerCase();
      }
      case 'added':
        return data.added ? new Date(data.added as any).getTime() : 0;
      case 'favorite':
        return data.favorite ? 1 : 0;
      default:
        return 0;
    }
  }

  applyFilter(value: string): void {
    this.dataSource.filter = value.trim().toLowerCase();
  }

  onInstallationClicked(installation_id: number): void {
    this.router.navigate(['/installation/' + installation_id + '/detail']);
  }

  getAutoLoginLabel(row: Installation): string | null {
    const hasInstallerVisualisationAccess = this.hasAutoLoginAccess(row);

    if (!hasInstallerVisualisationAccess) {
      return null;
    }

    return row.originId === 2
      ? 'installation_loginQcp'
      : 'installation_loginQc';
  }

  private hasAutoLoginAccess(row: Installation): boolean {
    const ownerId = row.owner?.id ?? 0;
    const ownerConfirmed = Boolean((row as any).ownerIdConfirmed);
    if (ownerId <= 0 || !ownerConfirmed) {
      return false;
    }

    this.authService.getUserId();
    if (this.authService.hasClaim('CanAccessInstallationsAll')) {
      return true;
    }

    const accessUntil = row.installerAccessUntil
      ? new Date(row.installerAccessUntil)
      : null;
    const hasValidLicense = accessUntil !== null && accessUntil > new Date();
    if (!hasValidLicense) {
      return false;
    }

    const isInstaller2ForRow = this.isCurrentUserInstaller2ForRow(row);
    const accessFlag = isInstaller2ForRow
      ? row.installer2VisualisationAccess
      : row.installerVisualisationAccess;

    return Boolean(accessFlag);
  }

  private isCurrentUserInstaller2ForRow(row: Installation): boolean {
    const currentUserId = this.authService.getUserId();
    if (!currentUserId) {
      return false;
    }

    return row.installer2?.id === currentUserId;
  }

  formatAddedDate(dateValue: string | Date | undefined): string {
    const date = dateValue ? new Date(dateValue) : null;
    return date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(this.qbusLocaleService.locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';
  }

  onAutoLogin(row: Installation): void {
    this.autoLoginService.getQbusControlAutoLoginUrl(row.id).subscribe({
      next: (url) => {
        window.open(url, '_blank');
      },
      error: () => {
        alert('Failed to get auto-login URL.');
      },
    });
  }
}
